import React, { useState, useEffect, useCallback } from 'react';
import { CalendarDays, Pencil, Trash2, Plus, ChevronDown, ChevronUp } from 'lucide-react';
import {
  fetchEvents, fetchEventSuggestions, createEvent, updateEvent, deleteEvent, fetchCinemas,
} from '../api/client';
import Poster from '../components/Poster';
import { useAuth } from '../context/AuthContext';
import useWindowWidth from '../hooks/useWindowWidth';

const formatNumber = (v) => (v || 0).toLocaleString();
const cardStyle = { background: 'var(--surf)', borderRadius: 16, boxShadow: 'var(--shadow)', padding: 20 };
const fmtD = (s) => { const [y, m, d] = s.split('-'); return `${d}/${m}/${y}`; };
const dateLabel = (ev) => ev.start_date === ev.end_date ? fmtD(ev.start_date) : `${fmtD(ev.start_date)} – ${fmtD(ev.end_date)}`;

const EMPTY_FORM = { name: '', start_date: '', end_date: '', description: '', chains: [] };

function ChainChips({ chains }) {
  if (!chains || chains.length === 0) {
    return <span style={pill('var(--accent-bg)', 'var(--accent)')}>כל הרשתות</span>;
  }
  return chains.map(c => <span key={c} style={pill('var(--surf-alt)', 'var(--muted)', '1px solid var(--brd)')}>{c}</span>);
}

const pill = (bg, color, border = 'none') => ({
  background: bg, color, border, borderRadius: 20, padding: '3px 10px',
  fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap', display: 'inline-block',
});

// Big stat block used inside an event card
function Stat({ label, value, color }) {
  return (
    <div style={{ textAlign: 'center', minWidth: 80 }}>
      <div style={{ fontSize: 22, fontWeight: 800, color: color || 'var(--txt)', letterSpacing: -0.5, fontVariantNumeric: 'tabular-nums', direction: 'ltr' }}>{value}</div>
      <div style={{ fontSize: 10.5, fontWeight: 600, color: 'var(--faint)', marginTop: 2 }}>{label}</div>
    </div>
  );
}

function EventCard({ ev, isAdmin, onEdit, onDelete, onMovieClick, mob }) {
  const [open, setOpen] = useState(false);
  const s = ev.stats;
  const lift = s?.lift_pct;
  const maxChain = s?.by_chain?.length ? Math.max(...s.by_chain.map(c => c.tickets)) : 0;
  return (
    <div style={{ ...cardStyle, padding: 0, overflow: 'hidden' }}>
      {/* Header */}
      <div style={{ padding: '16px 20px', display: 'flex', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 200 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 17, fontWeight: 800, color: 'var(--txt)' }}>{ev.name}</span>
            {ev.is_upcoming && <span style={pill('rgba(124,58,237,0.12)', '#7c3aed')}>אירוע עתידי</span>}
            {s?.is_ongoing && <span style={pill('rgba(22,163,74,0.12)', 'var(--ok)')}>מתקיים היום</span>}
          </div>
          <div style={{ fontSize: 12.5, color: 'var(--muted)', marginTop: 3, fontVariantNumeric: 'tabular-nums' }}>{dateLabel(ev)}</div>
          {ev.description && <div style={{ fontSize: 12, color: 'var(--faint)', marginTop: 4 }}>{ev.description}</div>}
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8 }}><ChainChips chains={ev.chains} /></div>
        </div>

        {s && (
          <div style={{ display: 'flex', gap: mob ? 12 : 22, alignItems: 'center', flexWrap: 'wrap' }}>
            <Stat label="כרטיסים" value={formatNumber(s.total_tickets)} color="var(--accent)" />
            <Stat label="לעומת יום רגיל" color={lift == null ? 'var(--faint)' : lift >= 0 ? 'var(--ok)' : 'var(--danger)'}
              value={lift == null ? '—' : `${lift >= 0 ? '+' : ''}${lift}%`} />
            <Stat label="הקרנות" value={formatNumber(s.total_screenings)} />
            {!mob && <Stat label="סרטים" value={s.movies_count} />}
          </div>
        )}

        <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
          {isAdmin && (
            <>
              <button onClick={onEdit} title="עריכה" style={iconBtn}><Pencil size={15} /></button>
              <button onClick={onDelete} title="מחיקה" style={{ ...iconBtn, color: 'var(--danger)' }}><Trash2 size={15} /></button>
            </>
          )}
          {s && (
            <button onClick={() => setOpen(o => !o)} title={open ? 'צמצום' : 'פירוט'} style={{ ...iconBtn, background: open ? 'var(--accent-bg)' : 'var(--surf-alt)', color: open ? 'var(--accent)' : 'var(--muted)' }}>
              {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            </button>
          )}
        </div>
      </div>

      {/* Expanded detail */}
      {open && s && (
        <div style={{ borderTop: '1px solid var(--brd)', padding: '14px 20px 18px', display: 'flex', gap: 24, flexWrap: 'wrap' }}>
          {/* Top movies */}
          {s.top_movies?.length > 0 && (
            <div style={{ flex: '1 1 260px', minWidth: 0 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--muted)', marginBottom: 10 }}>הסרטים המובילים באירוע</div>
              {s.top_movies.map((m, i) => (
                <div key={m.movie_id} onClick={() => onMovieClick && onMovieClick(m.movie_id)}
                  style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 0', borderBottom: i < s.top_movies.length - 1 ? '1px solid var(--brd)' : 'none', cursor: 'pointer' }}>
                  <div style={{ width: 18, textAlign: 'center', fontWeight: 800, fontSize: 12, color: i < 3 ? 'var(--gold)' : 'var(--faint)' }}>{i + 1}</div>
                  <Poster posterUrl={m.poster_url} genre={m.genre} letter={(m.title_he || m.title || '?')[0]} size="sm" />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--txt)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{m.title_he || m.title}</div>
                    {m.title_en && <div style={{ fontSize: 10, color: 'var(--faint)' }}>{m.title_en}</div>}
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--accent)' }}>{formatNumber(m.tickets)}</div>
                    <div style={{ fontSize: 9.5, color: 'var(--faint)' }}>{m.screenings} הקרנות</div>
                  </div>
                </div>
              ))}
            </div>
          )}
          {/* Chain breakdown */}
          {s.by_chain?.length > 0 && (
            <div style={{ flex: '1 1 220px', minWidth: 0 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--muted)', marginBottom: 10 }}>פילוח לפי רשת</div>
              {s.by_chain.map(c => (
                <div key={c.chain} style={{ marginBottom: 8 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11.5, marginBottom: 3 }}>
                    <span style={{ color: 'var(--txt)', fontWeight: 600 }}>{c.chain_he || c.chain}</span>
                    <span style={{ color: 'var(--muted)', fontVariantNumeric: 'tabular-nums' }}>{formatNumber(c.tickets)}</span>
                  </div>
                  <div style={{ height: 6, borderRadius: 4, background: 'var(--surf-alt)', overflow: 'hidden' }}>
                    <div style={{ height: '100%', borderRadius: 4, background: 'var(--accent)', width: `${maxChain ? Math.max(3, Math.round(c.tickets * 100 / maxChain)) : 0}%` }} />
                  </div>
                </div>
              ))}
              {s.baseline_tickets != null && (
                <div style={{ fontSize: 10.5, color: 'var(--faint)', marginTop: 10 }}>
                  ממוצע יום רגיל מקביל: {formatNumber(s.baseline_tickets)} כרטיסים
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const iconBtn = {
  background: 'var(--surf-alt)', border: '1px solid var(--brd)', borderRadius: 8,
  width: 30, height: 30, display: 'flex', alignItems: 'center', justifyContent: 'center',
  cursor: 'pointer', color: 'var(--muted)',
};

function EventsPage({ onMovieClick, refreshNonce }) {
  const { hasPermission } = useAuth();
  const isAdmin = hasPermission('events.manage');
  const mob = useWindowWidth() < 768;

  const [events, setEvents] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [chains, setChains] = useState([]);
  const [form, setForm] = useState(null); // null = closed; {..., id?} = open
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(() => {
    fetchEvents().then(d => setEvents(d?.events || [])).catch(() => setEvents([]));
    if (isAdmin) {
      fetchEventSuggestions().then(d => setSuggestions(d?.suggestions || [])).catch(() => setSuggestions([]));
    }
  }, [isAdmin]);

  useEffect(() => { load(); }, [load, refreshNonce]);

  useEffect(() => {
    if (!isAdmin) return;
    fetchCinemas().then(list => {
      const seen = new Map();
      (list || []).forEach(c => { if (c.chain && !seen.has(c.chain)) seen.set(c.chain, c.chain_he || c.chain); });
      setChains([...seen.entries()].map(([name, he]) => ({ name, he })));
    }).catch(() => setChains([]));
  }, [isAdmin]);

  const save = async () => {
    if (!form.name.trim() || !form.start_date) { setError('נדרשים שם ותאריך התחלה'); return; }
    setSaving(true); setError('');
    const body = {
      name: form.name, start_date: form.start_date,
      end_date: form.end_date || form.start_date,
      description: form.description || null,
      chains: form.chains.length ? form.chains : null,
    };
    try {
      const res = form.id ? await updateEvent(form.id, body) : await createEvent(body);
      if (res?.error) setError(res.error);
      else { setForm(null); load(); }
    } catch {
      setError('השמירה נכשלה');
    }
    setSaving(false);
  };

  const remove = async (ev) => {
    if (!window.confirm(`למחוק את "${ev.name}"?`)) return;
    try { await deleteEvent(ev.id); load(); } catch {}
  };

  const openForm = (preset) => { setError(''); setForm({ ...EMPTY_FORM, ...preset }); };

  const isDark = document.documentElement.dataset.theme === 'dark';
  const inputStyle = {
    background: 'var(--surf-alt)', border: '1px solid var(--brd)', borderRadius: 8,
    padding: '8px 12px', color: 'var(--txt)', fontSize: 13, fontFamily: 'Heebo, sans-serif',
    outline: 'none', colorScheme: isDark ? 'dark' : 'light', width: '100%', boxSizing: 'border-box',
  };

  if (events === null) {
    return <div style={{ textAlign: 'center', padding: 60, color: 'var(--faint)' }}>טוען אירועים...</div>;
  }

  const upcoming = events.filter(e => e.is_upcoming);
  const past = events.filter(e => !e.is_upcoming);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* Header + add button */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
        <div style={{ fontSize: 13, color: 'var(--faint)' }}>
          ימי קולנוע ומבצעים מיוחדים — והשפעתם על מכירות הכרטיסים
        </div>
        {isAdmin && !form && (
          <button onClick={() => openForm({})} style={{
            display: 'flex', alignItems: 'center', gap: 6, background: 'var(--accent)', color: '#fff',
            border: 'none', borderRadius: 10, padding: '9px 16px', fontSize: 13, fontWeight: 700,
            fontFamily: 'Heebo, sans-serif', cursor: 'pointer',
          }}><Plus size={16} /> אירוע חדש</button>
        )}
      </div>

      {/* Add/edit form */}
      {isAdmin && form && (
        <div style={{ ...cardStyle, display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--txt)' }}>{form.id ? 'עריכת אירוע' : 'אירוע חדש'}</div>
          <div style={{ display: 'grid', gridTemplateColumns: mob ? '1fr' : '2fr 1fr 1fr', gap: 10 }}>
            <input style={inputStyle} placeholder="שם האירוע (למשל: יום הקולנוע הישראלי)" value={form.name}
              onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
            <div>
              <div style={{ fontSize: 10.5, color: 'var(--faint)', marginBottom: 3 }}>מתאריך</div>
              <input type="date" style={inputStyle} value={form.start_date}
                onChange={e => setForm(f => ({ ...f, start_date: e.target.value }))} />
            </div>
            <div>
              <div style={{ fontSize: 10.5, color: 'var(--faint)', marginBottom: 3 }}>עד תאריך (ריק = יום אחד)</div>
              <input type="date" style={inputStyle} value={form.end_date}
                onChange={e => setForm(f => ({ ...f, end_date: e.target.value }))} />
            </div>
          </div>
          <input style={inputStyle} placeholder="תיאור (אופציונלי — למשל: כרטיס ב-10 ש״ח)" value={form.description}
            onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
          <div>
            <div style={{ fontSize: 10.5, color: 'var(--faint)', marginBottom: 6 }}>רשתות משתתפות (ללא בחירה = כל הרשתות)</div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {chains.map(c => {
                const on = form.chains.includes(c.name);
                return (
                  <button key={c.name} onClick={() => setForm(f => ({
                    ...f, chains: on ? f.chains.filter(x => x !== c.name) : [...f.chains, c.name],
                  }))} style={{
                    padding: '5px 12px', borderRadius: 20, fontSize: 12, fontFamily: 'Heebo, sans-serif',
                    fontWeight: on ? 700 : 400, cursor: 'pointer',
                    border: `1px solid ${on ? 'var(--accent)' : 'var(--brd)'}`,
                    background: on ? 'var(--accent)' : 'var(--surf-alt)', color: on ? '#fff' : 'var(--muted)',
                  }}>{c.he}</button>
                );
              })}
            </div>
          </div>
          {error && <div style={{ fontSize: 12, color: 'var(--danger)' }}>{error}</div>}
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={save} disabled={saving} style={{
              background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: 10,
              padding: '9px 22px', fontSize: 13, fontWeight: 700, fontFamily: 'Heebo, sans-serif',
              cursor: 'pointer', opacity: saving ? 0.6 : 1,
            }}>{saving ? 'שומר...' : 'שמירה'}</button>
            <button onClick={() => setForm(null)} style={{
              background: 'var(--surf-alt)', color: 'var(--muted)', border: '1px solid var(--brd)', borderRadius: 10,
              padding: '9px 18px', fontSize: 13, fontFamily: 'Heebo, sans-serif', cursor: 'pointer',
            }}>ביטול</button>
          </div>
        </div>
      )}

      {/* Suggestions strip (admin) */}
      {isAdmin && suggestions.length > 0 && (
        <div style={{ ...cardStyle, background: 'rgba(234,179,8,0.06)', border: '1px solid rgba(234,179,8,0.25)', boxShadow: 'none' }}>
          <div style={{ fontSize: 13.5, fontWeight: 700, color: '#b45309', marginBottom: 10 }}>
            ⚡ ימים עם מכירות חריגות שאינם משויכים לאירוע — אולי היה אירוע?
          </div>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            {suggestions.map(sg => (
              <div key={sg.date} style={{
                background: 'var(--surf)', border: '1px solid var(--brd)', borderRadius: 12,
                padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 12,
              }}>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--txt)', fontVariantNumeric: 'tabular-nums' }}>{fmtD(sg.date)}</div>
                  <div style={{ fontSize: 10.5, color: 'var(--faint)' }}>{formatNumber(sg.tickets)} כרטיסים · ממוצע {formatNumber(sg.baseline)}</div>
                </div>
                <span style={{ fontSize: 14, fontWeight: 800, color: 'var(--ok)', direction: 'ltr' }}>+{sg.lift_pct}%</span>
                <button onClick={() => openForm({ start_date: sg.date, end_date: sg.date })} style={{
                  background: 'var(--accent-bg)', color: 'var(--accent)', border: 'none', borderRadius: 8,
                  padding: '6px 12px', fontSize: 11.5, fontWeight: 700, fontFamily: 'Heebo, sans-serif', cursor: 'pointer',
                }}>צור אירוע</button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Upcoming events */}
      {upcoming.length > 0 && (
        <>
          <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--txt)', display: 'flex', alignItems: 'center', gap: 8 }}>
            <CalendarDays size={18} color="var(--accent)" /> אירועים קרובים
          </div>
          {upcoming.map(ev => (
            <EventCard key={ev.id} ev={ev} isAdmin={isAdmin} mob={mob}
              onEdit={() => openForm({ id: ev.id, name: ev.name, start_date: ev.start_date, end_date: ev.end_date, description: ev.description || '', chains: ev.chains || [] })}
              onDelete={() => remove(ev)} onMovieClick={onMovieClick} />
          ))}
        </>
      )}

      {/* Past events */}
      <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--txt)', marginTop: upcoming.length ? 8 : 0 }}>
        אירועים שהתקיימו
      </div>
      {past.length === 0 ? (
        <div style={{ ...cardStyle, textAlign: 'center', color: 'var(--faint)', padding: 40, fontSize: 13 }}>
          עדיין לא הוגדרו אירועים{isAdmin ? ' — הוסף אירוע חדש למעלה' : ''}
        </div>
      ) : past.map(ev => (
        <EventCard key={ev.id} ev={ev} isAdmin={isAdmin} mob={mob}
          onEdit={() => openForm({ id: ev.id, name: ev.name, start_date: ev.start_date, end_date: ev.end_date, description: ev.description || '', chains: ev.chains || [] })}
          onDelete={() => remove(ev)} onMovieClick={onMovieClick} />
      ))}
    </div>
  );
}

export default EventsPage;
